import { Link } from "@tanstack/react-router";
import { useState } from "react";
import { FileType } from "reedy-data/models";
import { useOrganisation } from "contexts/OrganisationProvider";
import { ArrowLeft } from "lucide-react";
import { useTranslation } from "react-i18next";
import { PaginationController } from "components/PaginationController/PaginationController";
import LoadingSpinner from "components/loading/LoadingSpinner";
import { usePipelineReports } from "hooks/usePipelineReports";
import { Text } from "components/text";
import { OrganisationOnixUploadFile } from "./components/OrganisationOnixUploadFile";
import { OrganisationOnixUploadFileReportDialog } from "./components/OrganisationOnixUploadFileReportDialog";

export const OrganisationOnixFTPUploadPage = () => {
  const [reportId, setReportId] = useState<string | null>(null);
  const { t } = useTranslation(["settings"]);

  const { id: organisationId } = useOrganisation();

  const { reports, loading, itemsCount } = usePipelineReports({
    mode: FileType.ONIX_IMPORT,
    organisationId,
  });

  return (
    <div>
      <div className="flex items-center text-primary-500 mb-6">
        <Link
          to="/settings/organisations/$organisationId/onix-upload"
          params={{ organisationId }}
          className="flex items-center gap-2 text-sm"
        >
          <ArrowLeft className="h-4 w-4 " />
          {t("organisation.onix.back")}
        </Link>
      </div>
      <div className="flex space-between align-center justify-center">
        <div className="grow">
          <Text as="h2" variant="pageHeading" className="mb-2">
            {t("settings:organisation.onix.ftp.title")}
          </Text>
          <Text variant="subtle" className="pb-4">
            {t("settings:organisation.onix.ftp.description")}
          </Text>
        </div>
      </div>
      {loading ? (
        <div className="flex items-center justify-center w-full pt-10">
          <LoadingSpinner />
        </div>
      ) : null}
      {!loading && reports.length === 0 ? (
        <Text variant="subtle" className="text-center">
          {t("settings:organisation.onix.ftp.empty")}
        </Text>
      ) : (
        <>
          <ul className="mt-8 flex flex-col gap-2">
            {reports.map((report) => (
              <OrganisationOnixUploadFile
                key={report.id}
                organisationId={organisationId}
                fileName={report.fileName}
                createdAt={new Date(report.createdAt)}
                report={report}
                onActionClick={setReportId}
              />
            ))}
          </ul>
          <div className="mt-4">
            <PaginationController
              itemsCount={itemsCount}
              pageSizeLabel={t("settings:organisation.onix.perPage")}
            />
          </div>
        </>
      )}
      {reportId && (
        <OrganisationOnixUploadFileReportDialog
          open={!!reportId}
          reportId={reportId}
          onClose={() => setReportId(null)}
        />
      )}
    </div>
  );
};
